// The grounding account a run produced, mapped from the server's shape into the one the breakdown
// panel renders.
//
// This replaces the client-side mirror that preceded it (`novaGroundingBreakdown.ts`), which
// recomputed a plausible account from the saved definition and the selected task. The account now
// comes from the assembly that actually ran, so the panel reports what the model was sent rather
// than what it would probably have been sent.

// Why a reference contributed nothing. One per reason the assembler actually produces; a reason this
// client does not know is left undescribed rather than guessed at.
export const NovaGroundingReason = {
	// The variable is bound to a field this task leaves empty. Expected and normal, unlike the
	// reasons below, which all point at something the author probably wants to fix.
	NoValue: 'NO_VALUE',
	// The variable exists but is not permitted in the input that referenced it, so it is dropped
	// rather than allowed to carry customer-derived data into the standing, trusted inputs.
	NotPermitted: 'NOT_PERMITTED',
	// The name was a variable once and has since been retired.
	RemovedVariable: 'REMOVED_VARIABLE',
	// The name was never a variable — almost always a typo.
	UnknownName: 'UNKNOWN_NAME',
	// A visibility gate held this back from the person the run is attributed to. The gate's own
	// reason is operator-facing text about permissions, so it never reaches the author — every
	// withheld entry reads as the one thing true of all of them.
	NotVisible: 'NOT_VISIBLE',
	// A `{file:…}` the run read no document for. Deliberately not worded as "no longer attached":
	// the assembler reports every file reference this way today, attached or not, because attaching a
	// document to a run is not yet wired up. What is true in both cases is that nothing was read.
	FileNotRead: 'FILE_NOT_READ',
} as const;
export type NovaGroundingReason = (typeof NovaGroundingReason)[keyof typeof NovaGroundingReason];

// The assembler's own words for why it dropped a reference. Its vocabulary, not ours — an entry
// carrying anything else is listed without a description.
const REASON_BY_SERVER_REASON: Record<string, NovaGroundingReason> = {
	'no resolved value': NovaGroundingReason.NoValue,
	'not permitted in this input': NovaGroundingReason.NotPermitted,
	removed: NovaGroundingReason.RemovedVariable,
	unknown: NovaGroundingReason.UnknownName,
	broken: NovaGroundingReason.FileNotRead,
};

export interface NovaGroundingEntry {
	token: string;
	// The authored input the reference was written in, where the account names one. Null on a
	// resolved or truncated entry: those are accounted per source read, not per mention.
	input: string | null;
	reason?: NovaGroundingReason;
	// Truncated entries only.
	keptTokens?: number;
	totalTokens?: number;
}

export interface NovaGroundingAccount {
	// Distinct sources that put something into the request, cut or whole.
	sourceCount: number;
	// What those sources contributed, as the run's own per-source estimates add up. The authored
	// prompt text around them is not counted: this figure answers "how much of this request is
	// pulled data", which is the question the panel is asking.
	approximateTokens: number;
	// Data that existed and did not fully reach the model. A reference to a field the task leaves
	// empty is not withholding — there was nothing to withhold.
	withheldCount: number;
	resolved: NovaGroundingEntry[];
	skipped: NovaGroundingEntry[];
	withheld: NovaGroundingEntry[];
	truncated: NovaGroundingEntry[];
	broken: NovaGroundingEntry[];
}

// The account as the run record serves it.
export interface NovaServerGroundingAccount {
	resolved: {token: string; estimatedTokens?: number | null}[];
	skipped: {token: string; input: string | null; reason: string}[];
	withheld: {token: string; reason: string}[];
	truncated: {token: string; keptTokens: number; estimatedTotalTokens: number}[];
	broken: {token: string; input: string | null; reason: string}[];
}

// The one skip reason that means the data was there and was kept back. `NoValue` had nothing to
// withhold, and an unrecognised reason says nothing either way.
function isWithholding(entry: NovaGroundingEntry): boolean {
	return entry.reason === NovaGroundingReason.NotPermitted;
}

function referenceEntry(entry: {token: string; input: string | null; reason: string}): NovaGroundingEntry {
	const reason = REASON_BY_SERVER_REASON[entry.reason];
	return reason === undefined ? {token: entry.token, input: entry.input} : {token: entry.token, input: entry.input, reason};
}

// Null in, null out: a run that recorded no account and a run that grounded in nothing are different
// things, and rendering an empty breakdown for the first would claim the second.
export function toGroundingAccount(account: NovaServerGroundingAccount | null | undefined): NovaGroundingAccount | null {
	if (account === null || account === undefined) {
		return null;
	}
	const resolved = account.resolved.map(entry => ({token: entry.token, input: null}));
	const truncated = account.truncated.map(entry => ({
		token: entry.token,
		input: null,
		keptTokens: entry.keptTokens,
		totalTokens: entry.estimatedTotalTokens,
	}));
	const skipped = account.skipped.map(referenceEntry);
	const withheld = account.withheld.map(entry => ({
		token: entry.token,
		input: null,
		reason: NovaGroundingReason.NotVisible,
	}));
	const contributedTokens =
		account.resolved.reduce((total, entry) => total + (entry.estimatedTokens ?? 0), 0) +
		account.truncated.reduce((total, entry) => total + entry.keptTokens, 0);

	return {
		sourceCount: resolved.length + truncated.length,
		approximateTokens: contributedTokens,
		// Counted from the reasons that mean data existed and did not fully reach the model, rather
		// than from everything that is not "no value here". A reason this build does not recognise is
		// neutral: counted, it would inflate a figure the lists cannot explain, since the entry itself
		// renders with no description at all.
		withheldCount: truncated.length + withheld.length + skipped.filter(isWithholding).length,
		resolved,
		skipped,
		withheld,
		truncated,
		broken: account.broken.map(referenceEntry),
	};
}
